import { Eye, Users } from 'lucide-react';
import { Button } from '../../ui/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/components/ui/dialog';
import { EmailIFramePreview, EmailLivePreview } from './components/live-preview';
import { SendMailForm } from './components/send-mail-form';
import { MailSenderResponseDto, MassMailMetaResponseDto, OrganizationRecipientResponseDto, PreviewResponseDto } from './types/response';
import { useCallback, useContext, useEffect, useState } from 'react';
import useApiRequest from '../../lib/hooks/useRequest';
import { PreviewRequestDto } from './types/request';
import { Context } from '../../components/root/context';
import { RecipientSelectionDialog } from './components/recipient-selection-dialog';
import { SendProgress } from './components/send-progress';

export const EmailPage = ({ mailMeta }: { mailMeta: MassMailMetaResponseDto }) => {
  const { apiRequest } = useApiRequest();
  const { accessToken } = useContext(Context);

  const [selectedSender, setSelectedSender] = useState<MailSenderResponseDto>(mailMeta.mailSenders[0]);
  const [selectedTemplate, setSelectedTemplate] = useState<string>(mailMeta.mailTemplates[0]);
  const [messageContent, setMessageContent] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [selectedRecipients, setSelectedRecipients] = useState<OrganizationRecipientResponseDto[]>([]);

  const [isRecipientDialogOpen, setIsRecipientDialogOpen] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [renderedTemplate, setRenderedTemplate] = useState<string>('');
  const [sendProgressOpen, setSendProgressOpen] = useState(false);
  const [sendRequestId, setSendRequestId] = useState<string | null>(null);

  const [availableHeight, setAvailableHeight] = useState<number>(window.innerHeight);

  const updateHeight = () => {
    const offsetTop = document.getElementById('email-preview-card')?.getBoundingClientRect().top ?? 0;
    setAvailableHeight(window.innerHeight - offsetTop - 60);
  };

  useEffect(() => {
    updateHeight();
  }, []);

  const fetchPreview = useCallback(
    async (template: string, content: string) => {
      setIsLoadingTemplate(true);

      try {
        const response = await apiRequest<PreviewResponseDto>('mass-mails/internal/preview', 'POST', {
          body: {
            template: template,
            content: content
          } as PreviewRequestDto
        });
        if (response.data) {
          setRenderedTemplate(response.data.preview);
        }
      } catch (error) {
        setRenderedTemplate('Beim laden des Templates ist ein Fehler aufgetreten.');
        console.log(error);
      } finally {
        setIsLoadingTemplate(false);
      }
    },
    [apiRequest]
  );

  useEffect(() => {
    if (!selectedTemplate || !messageContent.trim()) {
      setRenderedTemplate('');
      return;
    }

    // Debounce the preview fetch
    const timeoutId = setTimeout(() => {
      fetchPreview(selectedTemplate, messageContent);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [selectedTemplate, messageContent, fetchPreview]);

  return (
    <div className="flex flex-row gap-5 items-start">
      <div className="flex flex-col py-7 px-9 bg-white rounded-lg gap-[14px] w-2/5">
        <div className="flex items-center justify-between mb-2">
          <h5 className="font-semibold text-lg">E-Mail schreiben</h5>
          <Button variant="outline" size="sm" onClick={() => setIsRecipientDialogOpen(true)}>
            <Users className="w-4 h-4 mr-2" />
            {selectedRecipients.length > 0 ? `${selectedRecipients.length} Empfänger` : 'Empfänger auswählen'}
          </Button>
        </div>

        <SendMailForm
          mailMeta={mailMeta}
          selectedSender={selectedSender}
          setSelectedSender={setSelectedSender}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          messageContent={messageContent}
          setMessageContent={setMessageContent}
          subject={subject}
          setSubject={setSubject}
          selectedRecipients={selectedRecipients}
          onSend={(requestId) => {
            setSendRequestId(requestId);
            setSendProgressOpen(true);
          }}
        />
      </div>

      <div
        id="email-preview-card"
        className="flex flex-col py-7 px-9 bg-white rounded-lg gap-[14px] w-3/5"
        style={renderedTemplate ? { height: `${availableHeight}px` } : { maxHeight: `${availableHeight}px` }}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Live Vorschau</h3>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="flex items-center gap-1">
                <Eye className="w-3 h-3" />
                Vorschau
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl w-full h-[90vh] p-0 flex flex-col">
              <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
                <DialogTitle>E-Mail Vorschau</DialogTitle>
              </DialogHeader>
              <div className="flex-1 flex-grow flex flex-col">
                <EmailIFramePreview renderedTemplate={renderedTemplate} />
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="flex flex-col rounded-lg shadow-2xl overflow-hidden flex-grow">
          <EmailLivePreview
            sender={selectedSender}
            subject={subject}
            isLoadingTemplate={isLoadingTemplate}
            renderedTemplate={renderedTemplate}
            selectedRecipientsCount={selectedRecipients.length}
          />
        </div>
      </div>

      {/* Recipient Selection Dialog */}
      <RecipientSelectionDialog
        isOpen={isRecipientDialogOpen}
        orgaStatus={mailMeta.organizationStatuses}
        selectedRecipients={selectedRecipients}
        abortDialog={() => setIsRecipientDialogOpen(false)}
        handleFinish={(selectedRecipients: OrganizationRecipientResponseDto[]) => {
          setSelectedRecipients(selectedRecipients);
          setIsRecipientDialogOpen(false);
        }}
      />

      {/* Send Progress */}
      {sendRequestId && accessToken && (
        <SendProgress
          isOpen={sendProgressOpen}
          abortDialog={() => {
            setSendProgressOpen(false);
            setSendRequestId(null);
          }}
          abortAndSetRecipients={(recipients: OrganizationRecipientResponseDto[]) => {
            setSendProgressOpen(false);
            setSendRequestId(null);
            setSelectedRecipients(recipients);
          }}
          requestId={sendRequestId}
          accessToken={accessToken}
        />
      )}
    </div>
  );
};
